var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { wirePointerDrag } from '../../components/interaction';
import { getCardSideAnchor, getDiagramCardSize, getNodeBoxInParent, nodeLocalToParent } from '../coords';
import { connectNodes } from '../connectors';
import { collectObstacles } from '../router';
import { getActiveDiagram } from '../theme';
import { nearestPointOnPolyline } from '../pathUtils';
import { collectEditableNodes, collectEdgesFromLayer, findEdgeLayer, findNodeByDiagramId, nodeDiagramId, resolveEditableGroup } from './collect';
import { attachEdgeHitTarget, edgeAnchorPoint } from './edgeWiring';
import { showLabelEditor } from './labelEdit';
import { rerouteDiagramEdges, syncEdgesToState, syncPositionsToState, clearEdgeWaypointsForNode } from './reroute';
import { applyAnchoredResize, RESIZE_HANDLES } from './resize';
import { normalizeDegrees, pointerAngleDegrees, rotatedCardCenter, setRotationAroundCenter, snapDegrees, } from './rotate';
var HANDLE = 9;
var BEND_R = 6;
var ROTATE_STEM = 22;
var ROTATE_R = 6;
/** True if this node and every ancestor is visible. */
function isEffectivelyVisible(node) {
    var cur = node;
    while (cur) {
        if (cur.visible === false)
            return false;
        cur = cur.parent;
    }
    return true;
}
function resolveEditorFlags(options) {
    var _a, _b, _c, _d, _e;
    var arrange = options.mode === 'arrange';
    return {
        allowLabelEdit: (_a = options.allowLabelEdit) !== null && _a !== void 0 ? _a : !arrange,
        // Resize from edges/corners is available in arrange mode too
        allowResize: (_b = options.allowResize) !== null && _b !== void 0 ? _b : true,
        allowRotate: (_c = options.allowRotate) !== null && _c !== void 0 ? _c : true,
        allowConnect: (_d = options.allowConnect) !== null && _d !== void 0 ? _d : !arrange,
        allowBendPoints: (_e = options.allowBendPoints) !== null && _e !== void 0 ? _e : true,
    };
}
var DiagramEditor = /** @class */ (function () {
    function DiagramEditor(app, root, options) {
        if (options === void 0) { options = {}; }
        var _this = this;
        var _a;
        this.tool = 'select';
        this.selectedId = null;
        this.selectedEdgeId = null;
        this.connectFromId = null;
        this.previewLine = null;
        this.handlers = [];
        this.keyHandler = null;
        this.destroyed = false;
        this.dragRaf = 0;
        this.app = app;
        this.root = root;
        this.options = __assign({ gridSize: 8, showPorts: true }, options);
        this.flags = resolveEditorFlags(this.options);
        this.tool = this.flags.allowConnect ? ((_a = options.tool) !== null && _a !== void 0 ? _a : 'select') : 'select';
        this.overlay = app.group({ zIndex: 1000, listening: true });
        this.overlay.metadata.diagramEditorOverlay = true;
        app.stage.add(this.overlay);
        if (this.flags.allowConnect) {
            this.keyHandler = function (e) {
                if (e.key !== 'Delete' && e.key !== 'Backspace')
                    return;
                if (!_this.selectedEdgeId)
                    return;
                e.preventDefault();
                _this.deleteSelectedEdge();
            };
            window.addEventListener('keydown', this.keyHandler);
        }
        else if (this.flags.allowBendPoints) {
            // Arrange mode: Delete removes the last bend point on the selected wire
            this.keyHandler = function (e) {
                if (e.key !== 'Delete' && e.key !== 'Backspace')
                    return;
                if (!_this.selectedEdgeId)
                    return;
                e.preventDefault();
                _this.removeLastBendPoint(_this.selectedEdgeId);
            };
            window.addEventListener('keydown', this.keyHandler);
        }
    }
    DiagramEditor.prototype.setTool = function (tool) {
        if (!this.flags.allowConnect && tool === 'connect')
            return;
        this.tool = tool;
        this.connectFromId = null;
        this.clearPreview();
        this.refreshOverlay();
    };
    DiagramEditor.prototype.getTool = function () {
        return this.tool;
    };
    DiagramEditor.prototype.selectNode = function (id) {
        this.selectedId = id;
        this.selectedEdgeId = null;
        this.refreshOverlay();
        this.app.requestRender();
    };
    DiagramEditor.prototype.selectEdge = function (id) {
        this.selectedEdgeId = id;
        this.selectedId = null;
        this.connectFromId = null;
        this.refreshOverlay();
        this.app.requestRender();
    };
    DiagramEditor.prototype.getSelectedEdgeId = function () {
        return this.selectedEdgeId;
    };
    DiagramEditor.prototype.getSelectedNodeId = function () {
        return this.selectedId;
    };
    /**
     * After org collapse/expand (or any relayout that hides nodes): drop selection
     * chrome for hidden nodes so the dotted border doesn't linger in empty space.
     */
    DiagramEditor.prototype.afterStructureChange = function () {
        var _this = this;
        if (this.destroyed)
            return;
        if (this.selectedId) {
            var node = findNodeByDiagramId(this.root, this.selectedId);
            if (!node || !isEffectivelyVisible(node)) {
                this.selectedId = null;
            }
        }
        if (this.selectedEdgeId) {
            var edgeLayer = findEdgeLayer(this.root);
            var edges = edgeLayer ? collectEdgesFromLayer(edgeLayer) : [];
            var edge = edges.find(function (e) { return e.id === _this.selectedEdgeId; });
            if (!edge) {
                this.selectedEdgeId = null;
            }
            else {
                var from = findNodeByDiagramId(this.root, edge.from);
                var to = findNodeByDiagramId(this.root, edge.to);
                if (!from || !to || !isEffectivelyVisible(from) || !isEffectivelyVisible(to)) {
                    this.selectedEdgeId = null;
                }
            }
        }
        this.refreshOverlay();
        this.app.requestRender();
    };
    DiagramEditor.prototype.reroute = function () {
        rerouteDiagramEdges(this.app, this.root);
    };
    DiagramEditor.prototype.wireNode = function (node) {
        var _this = this;
        var id = nodeDiagramId(node);
        if (!id)
            return;
        var onClick = function (e) {
            e.stopPropagation();
            if (_this.flags.allowConnect && _this.tool === 'connect') {
                if (!_this.connectFromId) {
                    _this.connectFromId = id;
                    _this.selectNode(id);
                }
                else if (_this.connectFromId !== id) {
                    _this.addEdge(_this.connectFromId, id);
                    _this.connectFromId = null;
                    _this.selectNode(id);
                }
                return;
            }
            _this.selectNode(id);
        };
        var onDblClick = function (e) {
            e.stopPropagation();
            if (!_this.flags.allowLabelEdit)
                return;
            showLabelEditor(_this.app, node, function (text) {
                _this.updateNodeLabel(node, text);
                _this.emitChange();
                _this.app.requestRender();
            });
        };
        var onDragMove = function () {
            var _a;
            var grid = (_a = _this.options.gridSize) !== null && _a !== void 0 ? _a : 0;
            if (grid > 0) {
                node.x = Math.round(node.x / grid) * grid;
                node.y = Math.round(node.y / grid) * grid;
            }
            // Live intelligent reconnect — free ports + drop bends on wires touching this node
            if (_this.dragRaf)
                cancelAnimationFrame(_this.dragRaf);
            _this.dragRaf = requestAnimationFrame(function () {
                _this.dragRaf = 0;
                if (_this.destroyed)
                    return;
                var draggedId = nodeDiagramId(node);
                rerouteDiagramEdges(_this.app, _this.root, undefined, draggedId ? { clearWaypointsForNodes: [draggedId] } : undefined);
                // After live reroute, re-bind edge hit targets (nodes were rebuilt)
                _this.wireEdges();
                _this.refreshOverlay();
                _this.app.requestRender();
            });
        };
        var onDragEnd = function () {
            if (_this.dragRaf) {
                cancelAnimationFrame(_this.dragRaf);
                _this.dragRaf = 0;
            }
            var draggedId = nodeDiagramId(node);
            rerouteDiagramEdges(_this.app, _this.root, undefined, draggedId ? { clearWaypointsForNodes: [draggedId] } : undefined);
            syncPositionsToState(_this.root);
            syncEdgesToState(_this.root);
            _this.wireEdges();
            _this.refreshOverlay();
            _this.emitChange();
            _this.app.requestRender();
        };
        node.on('click', onClick);
        if (this.flags.allowLabelEdit) {
            node.on('dblclick', onDblClick);
        }
        node.on('dragmove', onDragMove);
        node.on('dragend', onDragEnd);
        this.handlers.push({ node: node, type: 'click', fn: onClick }, { node: node, type: 'dragmove', fn: onDragMove }, { node: node, type: 'dragend', fn: onDragEnd });
        if (this.flags.allowLabelEdit) {
            this.handlers.push({ node: node, type: 'dblclick', fn: onDblClick });
        }
    };
    DiagramEditor.prototype.wireEdges = function () {
        var e_1, _a;
        var _this = this;
        var _b, _c, _d, _e, _f;
        var edgeLayer = findEdgeLayer(this.root);
        if (!edgeLayer)
            return;
        if (!this.flags.allowConnect && !this.flags.allowBendPoints)
            return;
        // Edge layers are created listening:false; without this, hitTest skips all wires.
        edgeLayer.listening = true;
        // Drop handlers for edges destroyed by reroute (prevents leak on every drag frame)
        this.pruneDetachedEdgeHandlers(edgeLayer);
        var _loop_1 = function (child) {
            var from = (_b = child.metadata) === null || _b === void 0 ? void 0 : _b.edgeFrom;
            var to = (_c = child.metadata) === null || _c === void 0 ? void 0 : _c.edgeTo;
            if (!from || !to)
                return "continue";
            if ((_d = child.metadata) === null || _d === void 0 ? void 0 : _d.edgeEditorWired)
                return "continue";
            attachEdgeHitTarget(this_1.app, child);
            var edgeId = (_f = (_e = child.metadata) === null || _e === void 0 ? void 0 : _e.edgeId) !== null && _f !== void 0 ? _f : "".concat(from, "-").concat(to);
            var onClick = function (e) {
                e.stopPropagation();
                _this.selectEdge(edgeId);
            };
            var onDblClick = function (e) {
                var _a, _b;
                e.stopPropagation();
                if (!_this.flags.allowBendPoints)
                    return;
                var wx = (_a = e.worldX) !== null && _a !== void 0 ? _a : 0;
                var wy = (_b = e.worldY) !== null && _b !== void 0 ? _b : 0;
                _this.addBendPointAt(edgeId, wx, wy);
            };
            child.on('click', onClick);
            if (this_1.flags.allowBendPoints) {
                child.on('dblclick', onDblClick);
            }
            child.metadata.edgeEditorWired = true;
            this_1.handlers.push({ node: child, type: 'click', fn: onClick });
            if (this_1.flags.allowBendPoints) {
                this_1.handlers.push({ node: child, type: 'dblclick', fn: onDblClick });
            }
        };
        var this_1 = this;
        try {
            for (var _g = __values(edgeLayer.children), _j = _g.next(); !_j.done; _j = _g.next()) {
                var child = _j.value;
                _loop_1(child);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_j && !_j.done && (_a = _g.return)) _a.call(_g);
            }
            finally { if (e_1) throw e_1.error; }
        }
    };
    /** Remove listeners bound to edge nodes that were destroyed during reroute. */
    DiagramEditor.prototype.pruneDetachedEdgeHandlers = function (edgeLayer) {
        var e_2, _a;
        var _b, _c, _d;
        var live = new Set(edgeLayer.children);
        var kept = [];
        try {
            for (var _e = __values(this.handlers), _f = _e.next(); !_f.done; _f = _e.next()) {
                var h = _f.value;
                var isEdgeHandler = ((_b = h.node.metadata) === null || _b === void 0 ? void 0 : _b.edgeFrom) != null ||
                    ((_c = h.node.metadata) === null || _c === void 0 ? void 0 : _c.edgeId) != null ||
                    ((_d = h.node.metadata) === null || _d === void 0 ? void 0 : _d.edgeHitAttached) === true;
                if (!isEdgeHandler) {
                    kept.push(h);
                    continue;
                }
                if (live.has(h.node) && h.node.parent) {
                    kept.push(h);
                    continue;
                }
                try {
                    h.node.off(h.type, h.fn);
                }
                catch (_g) {
                    /* already destroyed */
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_f && !_f.done && (_a = _e.return)) _a.call(_e);
            }
            finally { if (e_2) throw e_2.error; }
        }
        this.handlers = kept;
    };
    DiagramEditor.prototype.deleteSelectedEdge = function () {
        var _this = this;
        if (!this.selectedEdgeId)
            return;
        var edgeLayer = findEdgeLayer(this.root);
        if (!edgeLayer)
            return;
        var remaining = collectEdgesFromLayer(edgeLayer).filter(function (e) { return e.id !== _this.selectedEdgeId; });
        rerouteDiagramEdges(this.app, this.root, remaining);
        syncEdgesToState(this.root);
        this.selectedEdgeId = null;
        this.wireEdges();
        this.refreshOverlay();
        this.emitChange();
        this.app.requestRender();
    };
    DiagramEditor.prototype.rewireEdgeEndpoint = function (edgeId, end, newNodeId) {
        var _a, _b;
        var edgeLayer = findEdgeLayer(this.root);
        if (!edgeLayer)
            return;
        var edges = collectEdgesFromLayer(edgeLayer);
        var edge = edges.find(function (e) { return e.id === edgeId; });
        if (!edge)
            return;
        var nextFrom = end === 'from' ? newNodeId : edge.from;
        var nextTo = end === 'to' ? newNodeId : edge.to;
        if (nextFrom === nextTo)
            return;
        var updated = edges.map(function (e) {
            return e.id === edgeId ? __assign(__assign({}, e), { from: nextFrom, to: nextTo, id: "e_".concat(nextFrom, "_").concat(nextTo, "_").concat(Date.now()) }) : e;
        });
        rerouteDiagramEdges(this.app, this.root, updated);
        syncEdgesToState(this.root);
        this.selectedEdgeId = (_b = (_a = updated.find(function (e) { return e.from === nextFrom && e.to === nextTo; })) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        this.wireEdges();
        this.refreshOverlay();
        this.emitChange();
        this.app.requestRender();
    };
    DiagramEditor.prototype.addEdge = function (from, to) {
        var edgeLayer = this.root.children.find(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.diagramEdgeLayer; });
        if (!edgeLayer)
            return;
        var id = "e_".concat(from, "_").concat(to, "_").concat(Date.now());
        var fromNode = findNodeByDiagramId(this.root, from);
        var toNode = findNodeByDiagramId(this.root, to);
        if (!fromNode || !toNode)
            return;
        var obstacles = collectObstacles(collectEditableNodes(this.root));
        edgeLayer.add(connectNodes(this.app, fromNode, toNode, obstacles, {
            parent: this.root,
            style: 'smart',
            stroke: getActiveDiagram().edge,
            glowColor: getActiveDiagram().edgeGlow,
            strokeWidth: getActiveDiagram().stroke.edge,
            cornerRadius: 10,
            edgeId: id,
            fromId: from,
            toId: to,
        }));
        syncEdgesToState(this.root);
        // Rebuild with fan-out so multiple wires from one side stay separated
        rerouteDiagramEdges(this.app, this.root);
        this.wireEdges();
        this.selectEdge(id);
        this.emitChange();
        this.app.requestRender();
    };
    DiagramEditor.prototype.updateNodeLabel = function (node, text) {
        var e_3, _a;
        var _b, _c, _d, _e;
        try {
            for (var _f = __values(node.children), _g = _f.next(); !_g.done; _g = _f.next()) {
                var child = _g.value;
                if (child.type === 'text' && 'text' in child) {
                    child.text = text;
                    (_b = child.markDirty) === null || _b === void 0 ? void 0 : _b.call(child);
                }
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_a = _f.return)) _a.call(_f);
            }
            finally { if (e_3) throw e_3.error; }
        }
        var state = __assign({}, (_c = this.root.metadata) === null || _c === void 0 ? void 0 : _c.diagramState);
        var type = (_d = this.root.metadata) === null || _d === void 0 ? void 0 : _d.diagramType;
        var id = (_e = node.metadata) === null || _e === void 0 ? void 0 : _e.diagramId;
        if ((type === 'flowchart' || type === 'networkTopology') && state.data) {
            var data = state.data;
            var n = data.nodes.find(function (x) { return x.id === id; });
            if (n)
                n.label = text;
            state.data = data;
        }
        this.root.metadata.diagramState = state;
    };
    DiagramEditor.prototype.refreshOverlay = function () {
        var e_4, _a, e_5, _b;
        var _this = this;
        this.overlay.clear();
        if (this.selectedEdgeId && (this.flags.allowConnect || this.flags.allowBendPoints)) {
            this.drawEdgeSelection(this.selectedEdgeId);
            return;
        }
        if (!this.selectedId)
            return;
        var node = findNodeByDiagramId(this.root, this.selectedId);
        // Don't leave a dotted selection box in blank space after minimize
        if (!node || !isEffectivelyVisible(node)) {
            this.selectedId = null;
            return;
        }
        // Rotation-aware bounds in diagram-local space, then to stage
        var localBox = getNodeBoxInParent(node, this.root);
        var tl = this.rootLocalToStage(localBox.x, localBox.y);
        var br = this.rootLocalToStage(localBox.x + localBox.width, localBox.y + localBox.height);
        var wx = Math.min(tl.x, br.x);
        var wy = Math.min(tl.y, br.y);
        var w = Math.abs(br.x - tl.x);
        var h = Math.abs(br.y - tl.y);
        // Tight AABB around the rotated card
        this.overlay.add(this.app.rect({
            x: wx - 3,
            y: wy - 3,
            width: w + 6,
            height: h + 6,
            fill: null,
            stroke: getActiveDiagram().mindBranch.stroke,
            strokeWidth: 1.5,
            dash: [5, 4],
            listening: false,
        }));
        // Rotated card outline (follows the symbol, not just the AABB)
        var card = getDiagramCardSize(node);
        if (card) {
            var corners = [
                nodeLocalToParent(node, this.root, 0, 0),
                nodeLocalToParent(node, this.root, card.width, 0),
                nodeLocalToParent(node, this.root, card.width, card.height),
                nodeLocalToParent(node, this.root, 0, card.height),
            ].map(function (p) { return _this.rootLocalToStage(p.x, p.y); });
            var pts = [];
            try {
                for (var corners_1 = __values(corners), corners_1_1 = corners_1.next(); !corners_1_1.done; corners_1_1 = corners_1.next()) {
                    var c = corners_1_1.value;
                    pts.push(c.x, c.y);
                }
            }
            catch (e_4_1) { e_4 = { error: e_4_1 }; }
            finally {
                try {
                    if (corners_1_1 && !corners_1_1.done && (_a = corners_1.return)) _a.call(corners_1);
                }
                finally { if (e_4) throw e_4.error; }
            }
            pts.push(corners[0].x, corners[0].y);
            this.overlay.add(this.app.polyline({
                points: pts,
                fill: null,
                stroke: getActiveDiagram().mindBranch.stroke,
                strokeWidth: 1.25,
                opacity: 0.85,
                listening: false,
            }));
        }
        try {
            // Connection ports on the real (rotated) mid-sides
            for (var _c = __values(['top', 'right', 'bottom', 'left']), _d = _c.next(); !_d.done; _d = _c.next()) {
                var side = _d.value;
                var p = getCardSideAnchor(node, this.root, side);
                var s = this.rootLocalToStage(p.x, p.y);
                this.overlay.add(this.app.circle({
                    x: s.x - 3.5,
                    y: s.y - 3.5,
                    radius: 3.5,
                    fill: getActiveDiagram().edge,
                    stroke: '#fff',
                    strokeWidth: 1.25,
                    listening: false,
                }));
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
            }
            finally { if (e_5) throw e_5.error; }
        }
        if (this.flags.allowResize && this.tool === 'select') {
            this.addResizeHandles(node, wx, wy, w, h);
        }
        if (this.flags.allowRotate && this.tool === 'select') {
            this.addRotateHandle(node, wx, wy, w, h);
        }
        if (this.flags.allowConnect && this.tool === 'connect' && this.options.showPorts) {
            this.addPorts(node, wx, wy, w, h);
        }
    };
    DiagramEditor.prototype.drawEdgeSelection = function (edgeId) {
        var _this = this;
        var _a, _b, _c;
        var edgeLayer = findEdgeLayer(this.root);
        if (!edgeLayer)
            return;
        var edgeGroup = edgeLayer.children.find(function (c) { var _a, _b, _c, _d; return ((_b = (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.edgeId) !== null && _b !== void 0 ? _b : "".concat((_c = c.metadata) === null || _c === void 0 ? void 0 : _c.edgeFrom, "-").concat((_d = c.metadata) === null || _d === void 0 ? void 0 : _d.edgeTo)) === edgeId; });
        var record = collectEdgesFromLayer(edgeLayer).find(function (e) { return e.id === edgeId; });
        if (!record)
            return;
        // Highlight the actual wire path when available
        var pathPts = (_b = (_a = edgeGroup === null || edgeGroup === void 0 ? void 0 : edgeGroup.metadata) === null || _a === void 0 ? void 0 : _a.edgePoints) !== null && _b !== void 0 ? _b : [];
        if (pathPts.length >= 4) {
            var stagePts = [];
            for (var i = 0; i < pathPts.length; i += 2) {
                var s = this.rootLocalToStage(pathPts[i], pathPts[i + 1]);
                stagePts.push(s.x, s.y);
            }
            this.overlay.add(this.app.polyline({
                points: stagePts,
                fill: null,
                stroke: getActiveDiagram().mindBranch.stroke,
                strokeWidth: 3,
                opacity: 0.55,
                lineJoin: 'round',
                lineCap: 'round',
                listening: false,
            }));
        }
        if (this.flags.allowBendPoints) {
            var wps = (_c = record.waypoints) !== null && _c !== void 0 ? _c : [];
            wps.forEach(function (wp, index) {
                var stage = _this.rootLocalToStage(wp.x, wp.y);
                _this.addBendHandle(stage.x, stage.y, edgeId, index);
            });
        }
        if (this.flags.allowConnect) {
            var fromNode = findNodeByDiagramId(this.root, record.from);
            var toNode = findNodeByDiagramId(this.root, record.to);
            if (fromNode && toNode) {
                var fromPt = edgeAnchorPoint(this.root, fromNode, toNode, 'from');
                var toPt = edgeAnchorPoint(this.root, fromNode, toNode, 'to');
                this.addEndpointHandle(fromPt.x, fromPt.y, 'from', edgeId);
                this.addEndpointHandle(toPt.x, toPt.y, 'to', edgeId);
            }
        }
    };
    /** Diagram-local → stage/world (accounts for fitToBounds scale/offset). */
    DiagramEditor.prototype.rootLocalToStage = function (lx, ly) {
        var sx = this.root.scaleX || 1;
        var sy = this.root.scaleY || 1;
        return { x: this.root.x + lx * sx, y: this.root.y + ly * sy };
    };
    /** Stage/world → diagram-local. */
    DiagramEditor.prototype.stageToRootLocal = function (wx, wy) {
        var sx = this.root.scaleX || 1;
        var sy = this.root.scaleY || 1;
        return { x: (wx - this.root.x) / sx, y: (wy - this.root.y) / sy };
    };
    DiagramEditor.prototype.findEdgeGroup = function (edgeId) {
        var edgeLayer = findEdgeLayer(this.root);
        if (!edgeLayer)
            return undefined;
        return edgeLayer.children.find(function (c) {
            var _a, _b, _c, _d;
            var id = (_b = (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.edgeId) !== null && _b !== void 0 ? _b : "".concat((_c = c.metadata) === null || _c === void 0 ? void 0 : _c.edgeFrom, "-").concat((_d = c.metadata) === null || _d === void 0 ? void 0 : _d.edgeTo);
            return id === edgeId;
        });
    };
    DiagramEditor.prototype.updateEdgeWaypoints = function (edgeId, waypoints, opts) {
        var edgeLayer = findEdgeLayer(this.root);
        if (!edgeLayer)
            return;
        var edges = collectEdgesFromLayer(edgeLayer).map(function (e) {
            return e.id === edgeId
                ? __assign(__assign({}, e), { waypoints: waypoints.map(function (w) { return ({ x: w.x, y: w.y }); }), options: __assign(__assign({}, e.options), { waypoints: waypoints.map(function (w) { return ({ x: w.x, y: w.y }); }) }) }) : e;
        });
        rerouteDiagramEdges(this.app, this.root, edges);
        if (!(opts === null || opts === void 0 ? void 0 : opts.live)) {
            syncEdgesToState(this.root);
            this.wireEdges();
            this.refreshOverlay();
        }
        else {
            // Rebuild wires under the handles; keep overlay so the active bend drag isn't destroyed
            this.wireEdges();
        }
        this.app.requestRender();
    };
    DiagramEditor.prototype.addBendPointAt = function (edgeId, stageX, stageY) {
        var _a, _b, _c, _d;
        var edge = this.findEdgeGroup(edgeId);
        if (!edge)
            return;
        var local = this.stageToRootLocal(stageX, stageY);
        var path = (_b = (_a = edge.metadata) === null || _a === void 0 ? void 0 : _a.edgePoints) !== null && _b !== void 0 ? _b : [];
        var current = ((_d = (_c = edge.metadata) === null || _c === void 0 ? void 0 : _c.edgeWaypoints) !== null && _d !== void 0 ? _d : []).slice();
        var point = { x: local.x, y: local.y };
        if (path.length >= 4) {
            var near = nearestPointOnPolyline(path, local.x, local.y);
            point = { x: near.x, y: near.y };
        }
        current.push(point);
        var ordered = this.orderWaypointsAlongEdge(edgeId, current);
        this.selectEdge(edgeId);
        this.updateEdgeWaypoints(edgeId, ordered);
        this.emitChange();
    };
    DiagramEditor.prototype.orderWaypointsAlongEdge = function (edgeId, waypoints) {
        var _a, _b;
        var edge = this.findEdgeGroup(edgeId);
        var path = (_b = (_a = edge === null || edge === void 0 ? void 0 : edge.metadata) === null || _a === void 0 ? void 0 : _a.edgePoints) !== null && _b !== void 0 ? _b : [];
        if (path.length < 4 || waypoints.length <= 1)
            return waypoints.slice();
        var score = function (wx, wy) {
            var bestT = 0;
            var bestD = Infinity;
            var acc = 0;
            for (var i = 0; i < path.length - 2; i += 2) {
                var x0 = path[i];
                var y0 = path[i + 1];
                var x1 = path[i + 2];
                var y1 = path[i + 3];
                var segLen = Math.hypot(x1 - x0, y1 - y0);
                var dx = x1 - x0;
                var dy = y1 - y0;
                var lenSq = dx * dx + dy * dy;
                var t = lenSq < 1e-8 ? 0 : Math.max(0, Math.min(1, ((wx - x0) * dx + (wy - y0) * dy) / lenSq));
                var px = x0 + dx * t;
                var py = y0 + dy * t;
                var d = Math.hypot(wx - px, wy - py);
                if (d < bestD) {
                    bestD = d;
                    bestT = acc + segLen * t;
                }
                acc += segLen;
            }
            return bestT;
        };
        return waypoints.slice().sort(function (a, b) { return score(a.x, a.y) - score(b.x, b.y); });
    };
    DiagramEditor.prototype.removeLastBendPoint = function (edgeId) {
        var _a, _b;
        var edge = this.findEdgeGroup(edgeId);
        if (!edge)
            return;
        var current = ((_b = (_a = edge.metadata) === null || _a === void 0 ? void 0 : _a.edgeWaypoints) !== null && _b !== void 0 ? _b : []).slice();
        if (current.length === 0)
            return;
        current.pop();
        this.updateEdgeWaypoints(edgeId, current);
        this.emitChange();
    };
    DiagramEditor.prototype.addBendHandle = function (stageX, stageY, edgeId, index) {
        var _this = this;
        var handle = this.app.circle({
            x: stageX - BEND_R,
            y: stageY - BEND_R,
            radius: BEND_R,
            fill: '#fff',
            stroke: getActiveDiagram().mindBranch.stroke,
            strokeWidth: 2,
            listening: true,
        });
        handle.metadata.diagramEditorOverlay = true;
        handle.metadata.edgeBendIndex = index;
        this.overlay.add(handle);
        var liveWps = null;
        wirePointerDrag(handle, function (wx, wy) {
            var _a, _b, _c;
            var local = _this.stageToRootLocal(wx, wy);
            var grid = (_a = _this.options.gridSize) !== null && _a !== void 0 ? _a : 0;
            if (grid > 0) {
                local.x = Math.round(local.x / grid) * grid;
                local.y = Math.round(local.y / grid) * grid;
            }
            if (!liveWps) {
                var edge = _this.findEdgeGroup(edgeId);
                liveWps = ((_c = (_b = edge === null || edge === void 0 ? void 0 : edge.metadata) === null || _b === void 0 ? void 0 : _b.edgeWaypoints) !== null && _c !== void 0 ? _c : []).map(function (w) { return ({ x: w.x, y: w.y }); });
            }
            if (!liveWps[index])
                return;
            liveWps[index] = { x: local.x, y: local.y };
            // Move handle immediately for snappy UX
            handle.x = _this.rootLocalToStage(local.x, local.y).x - BEND_R;
            handle.y = _this.rootLocalToStage(local.x, local.y).y - BEND_R;
            if (_this.dragRaf)
                cancelAnimationFrame(_this.dragRaf);
            var snapshot = liveWps.map(function (w) { return ({ x: w.x, y: w.y }); });
            _this.dragRaf = requestAnimationFrame(function () {
                _this.dragRaf = 0;
                if (_this.destroyed)
                    return;
                _this.updateEdgeWaypoints(edgeId, snapshot, { live: true });
            });
        }, function () {
            if (_this.dragRaf) {
                cancelAnimationFrame(_this.dragRaf);
                _this.dragRaf = 0;
            }
            if (liveWps) {
                _this.updateEdgeWaypoints(edgeId, liveWps);
                _this.emitChange();
            }
            liveWps = null;
        });
        // Double-click bend handle to remove that point
        var onDbl = function (e) {
            var _a, _b;
            e.stopPropagation();
            var edge = _this.findEdgeGroup(edgeId);
            var wps = ((_b = (_a = edge === null || edge === void 0 ? void 0 : edge.metadata) === null || _a === void 0 ? void 0 : _a.edgeWaypoints) !== null && _b !== void 0 ? _b : []).slice();
            wps.splice(index, 1);
            _this.updateEdgeWaypoints(edgeId, wps);
            _this.emitChange();
        };
        handle.on('dblclick', onDbl);
        this.handlers.push({ node: handle, type: 'dblclick', fn: onDbl });
    };
    DiagramEditor.prototype.addEndpointHandle = function (x, y, end, edgeId) {
        var _this = this;
        var handle = this.app.circle({
            x: x,
            y: y,
            radius: 6,
            fill: end === 'to' ? getActiveDiagram().edge : '#fff',
            stroke: getActiveDiagram().mindBranch.stroke,
            strokeWidth: 2,
            listening: true,
        });
        handle.metadata.diagramEditorOverlay = true;
        this.overlay.add(handle);
        var lastX = x;
        var lastY = y;
        wirePointerDrag(handle, function (wx, wy) {
            lastX = wx;
            lastY = wy;
            _this.drawPreviewLine(x, y, wx, wy);
        }, function () {
            var _a;
            _this.clearPreview();
            var hit = (_a = _this.app.hitTest(lastX, lastY)) === null || _a === void 0 ? void 0 : _a.node;
            var target = resolveEditableGroup(hit);
            if (target) {
                var nodeId = nodeDiagramId(target);
                if (nodeId)
                    _this.rewireEdgeEndpoint(edgeId, end, nodeId);
            }
            _this.app.requestRender();
        });
    };
    DiagramEditor.prototype.addResizeHandles = function (node, x, y, w, h) {
        var e_6, _a;
        var _this = this;
        var _b, _c, _d, _e, _f, _g, _j, _k, _l, _m;
        var cardW = (_f = (_e = ((_c = (_b = node.metadata) === null || _b === void 0 ? void 0 : _b.orgCardWidth) !== null && _c !== void 0 ? _c : (_d = node.metadata) === null || _d === void 0 ? void 0 : _d.diagramCardWidth)) !== null && _e !== void 0 ? _e : node.metadata.editorBaseW) !== null && _f !== void 0 ? _f : w;
        var cardH = (_m = (_l = ((_j = (_g = node.metadata) === null || _g === void 0 ? void 0 : _g.orgCardHeight) !== null && _j !== void 0 ? _j : (_k = node.metadata) === null || _k === void 0 ? void 0 : _k.diagramCardHeight)) !== null && _l !== void 0 ? _l : node.metadata.editorBaseH) !== null && _m !== void 0 ? _m : h;
        var safeCardW = Math.max(cardW, 1);
        var safeCardH = Math.max(cardH, 1);
        // Visual box in diagram-local space (accounts for fitToBounds via stage↔local)
        var startLocal = this.stageToRootLocal(x, y);
        var startBR = this.stageToRootLocal(x + w, y + h);
        var startBox = {
            x: startLocal.x,
            y: startLocal.y,
            width: Math.max(startBR.x - startLocal.x, 1),
            height: Math.max(startBR.y - startLocal.y, 1),
        };
        var startNodeX = node.x;
        var startNodeY = node.y;
        var _loop_2 = function (spec) {
            var hx = x + w * spec.u;
            var hy = y + h * spec.v;
            var handle = this_2.app.rect({
                x: hx - HANDLE / 2,
                y: hy - HANDLE / 2,
                width: HANDLE,
                height: HANDLE,
                fill: '#fff',
                stroke: getActiveDiagram().mindBranch.stroke,
                strokeWidth: 1.5,
                listening: true,
                cornerRadius: 2,
            });
            handle.metadata.diagramEditorOverlay = true;
            handle.metadata.resizeHandle = spec.id;
            this_2.overlay.add(handle);
            var handleId = spec.id;
            wirePointerDrag(handle, function (worldX, worldY) {
                var local = _this.stageToRootLocal(worldX, worldY);
                var next = applyAnchoredResize(startBox, handleId, local.x, local.y);
                var scaleX = Math.max(0.35, Math.min(4, next.width / safeCardW));
                var scaleY = Math.max(0.35, Math.min(4, next.height / safeCardH));
                node.scaleX = scaleX;
                node.scaleY = scaleY;
                // Keep the anchored edge/corner fixed by shifting the node
                node.x = startNodeX + (next.x - startBox.x);
                node.y = startNodeY + (next.y - startBox.y);
                node.markDirty();
                var resizedId = nodeDiagramId(node);
                rerouteDiagramEdges(_this.app, _this.root, undefined, resizedId ? { clearWaypointsForNodes: [resizedId] } : undefined);
                _this.refreshOverlay();
                _this.app.requestRender();
            }, function () {
                var resizedId = nodeDiagramId(node);
                rerouteDiagramEdges(_this.app, _this.root, undefined, resizedId ? { clearWaypointsForNodes: [resizedId] } : undefined);
                syncPositionsToState(_this.root);
                syncEdgesToState(_this.root);
                _this.wireEdges();
                _this.refreshOverlay();
                _this.emitChange();
                _this.app.requestRender();
            });
        };
        var this_2 = this;
        try {
            for (var RESIZE_HANDLES_1 = __values(RESIZE_HANDLES), RESIZE_HANDLES_1_1 = RESIZE_HANDLES_1.next(); !RESIZE_HANDLES_1_1.done; RESIZE_HANDLES_1_1 = RESIZE_HANDLES_1.next()) {
                var spec = RESIZE_HANDLES_1_1.value;
                _loop_2(spec);
            }
        }
        catch (e_6_1) { e_6 = { error: e_6_1 }; }
        finally {
            try {
                if (RESIZE_HANDLES_1_1 && !RESIZE_HANDLES_1_1.done && (_a = RESIZE_HANDLES_1.return)) _a.call(RESIZE_HANDLES_1);
            }
            finally { if (e_6) throw e_6.error; }
        }
    };
    DiagramEditor.prototype.addRotateHandle = function (node, x, y, w, h) {
        var _this = this;
        var _a, _b, _c, _d, _e, _f, _g, _j, _k, _l;
        var cardW = (_e = (_d = ((_b = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.orgCardWidth) !== null && _b !== void 0 ? _b : (_c = node.metadata) === null || _c === void 0 ? void 0 : _c.diagramCardWidth)) !== null && _d !== void 0 ? _d : node.metadata.editorBaseW) !== null && _e !== void 0 ? _e : w;
        var cardH = (_l = (_k = ((_g = (_f = node.metadata) === null || _f === void 0 ? void 0 : _f.orgCardHeight) !== null && _g !== void 0 ? _g : (_j = node.metadata) === null || _j === void 0 ? void 0 : _j.diagramCardHeight)) !== null && _k !== void 0 ? _k : node.metadata.editorBaseH) !== null && _l !== void 0 ? _l : h;
        var scaledW = Math.max(1, cardW * node.scaleX);
        var scaledH = Math.max(1, cardH * node.scaleY);
        var stemTop = y - ROTATE_STEM;
        var midX = x + w / 2;
        this.overlay.add(this.app.line({
            x: midX,
            y: y,
            x2: 0,
            y2: -ROTATE_STEM,
            stroke: getActiveDiagram().mindBranch.stroke,
            strokeWidth: 1.25,
            listening: false,
        }));
        var handle = this.app.circle({
            x: midX - ROTATE_R,
            y: stemTop - ROTATE_R,
            radius: ROTATE_R,
            fill: '#fff',
            stroke: getActiveDiagram().mindBranch.stroke,
            strokeWidth: 1.5,
            listening: true,
        });
        handle.metadata.diagramEditorOverlay = true;
        handle.metadata.rotateHandle = true;
        this.overlay.add(handle);
        wirePointerDrag(handle, function (worldX, worldY, _event, pointer) {
            var local = _this.stageToRootLocal(worldX, worldY);
            var center = rotatedCardCenter(node.x, node.y, scaledW, scaledH, node.rotation);
            // Handle sits above the box → angle from center to handle at rest is -90°
            var deg = pointerAngleDegrees(center.x, center.y, local.x, local.y) + 90;
            var free = !!(pointer && (pointer.shiftKey || pointer.metaKey));
            if (!free)
                deg = snapDegrees(deg, 15);
            else
                deg = normalizeDegrees(deg);
            setRotationAroundCenter(node, deg, scaledW, scaledH);
            var id = nodeDiagramId(node);
            if (id)
                clearEdgeWaypointsForNode(_this.root, id);
            rerouteDiagramEdges(_this.app, _this.root);
            _this.refreshOverlay();
            _this.app.requestRender();
        }, function () {
            var id = nodeDiagramId(node);
            if (id)
                clearEdgeWaypointsForNode(_this.root, id);
            syncPositionsToState(_this.root);
            rerouteDiagramEdges(_this.app, _this.root);
            syncEdgesToState(_this.root);
            _this.wireEdges();
            _this.refreshOverlay();
            _this.emitChange();
            _this.app.requestRender();
        });
    };
    DiagramEditor.prototype.addPorts = function (node, _x, _y, _w, _h) {
        var e_7, _a;
        var _this = this;
        var fromId = nodeDiagramId(node);
        var _loop_3 = function (side) {
            var p = getCardSideAnchor(node, this_3.root, side);
            var s = this_3.rootLocalToStage(p.x, p.y);
            var port = this_3.app.circle({
                x: s.x,
                y: s.y,
                radius: 5,
                fill: getActiveDiagram().edge,
                stroke: '#fff',
                strokeWidth: 1.5,
                listening: true,
            });
            port.metadata.diagramEditorOverlay = true;
            this_3.overlay.add(port);
            var lastX = s.x;
            var lastY = s.y;
            wirePointerDrag(port, function (wx, wy) {
                lastX = wx;
                lastY = wy;
                _this.drawPreviewLine(s.x, s.y, wx, wy);
            }, function () {
                var _a;
                _this.clearPreview();
                var hit = (_a = _this.app.hitTest(lastX, lastY)) === null || _a === void 0 ? void 0 : _a.node;
                var target = resolveEditableGroup(hit);
                if (target && fromId) {
                    var toId = nodeDiagramId(target);
                    if (toId && toId !== fromId) {
                        _this.addEdge(fromId, toId);
                        _this.selectNode(toId);
                    }
                }
                _this.app.requestRender();
            });
        };
        var this_3 = this;
        try {
            for (var _b = __values(['top', 'right', 'bottom', 'left']), _c = _b.next(); !_c.done; _c = _b.next()) {
                var side = _c.value;
                _loop_3(side);
            }
        }
        catch (e_7_1) { e_7 = { error: e_7_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_7) throw e_7.error; }
        }
    };
    DiagramEditor.prototype.drawPreviewLine = function (x1, y1, x2, y2) {
        this.clearPreview();
        this.previewLine = this.app.line({
            x: x1,
            y: y1,
            x2: x2,
            y2: y2,
            stroke: getActiveDiagram().edge,
            strokeWidth: 2,
            dash: [6, 4],
            listening: false,
        });
        this.previewLine.metadata.diagramEditorOverlay = true;
        this.overlay.add(this.previewLine);
        this.app.requestRender();
    };
    DiagramEditor.prototype.clearPreview = function () {
        if (this.previewLine) {
            this.overlay.remove(this.previewLine);
            this.previewLine.destroy();
            this.previewLine = null;
        }
    };
    DiagramEditor.prototype.emitChange = function () {
        var _a, _b, _c;
        (_b = (_a = this.options).onChange) === null || _b === void 0 ? void 0 : _b.call(_a, (_c = this.root.metadata) === null || _c === void 0 ? void 0 : _c.diagramState);
    };
    DiagramEditor.prototype.destroy = function () {
        var e_8, _a;
        if (this.destroyed)
            return;
        this.destroyed = true;
        if (this.dragRaf) {
            cancelAnimationFrame(this.dragRaf);
            this.dragRaf = 0;
        }
        if (this.keyHandler) {
            window.removeEventListener('keydown', this.keyHandler);
            this.keyHandler = null;
        }
        try {
            for (var _b = __values(this.handlers), _c = _b.next(); !_c.done; _c = _b.next()) {
                var h = _c.value;
                h.node.off(h.type, h.fn);
            }
        }
        catch (e_8_1) { e_8 = { error: e_8_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_8) throw e_8.error; }
        }
        this.handlers = [];
        this.overlay.destroy();
        this.app.requestRender();
    };
    return DiagramEditor;
}());
export { DiagramEditor };
